/**
 * index.tsx
 *
 * This is the entry file for the application, only setup and boilerplate
 * code.
 */

import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import 'sanitize.css/sanitize.css';

// Import root app
import { App } from 'app';

import { HelmetProvider } from 'react-helmet-async';

import { configureAppStore } from 'store/configureStore';

// Initialize languages
import { ToastContainer } from '@mti/multitenant-ui-shared';
import { StyledEngineProvider } from '@mui/material';
import { LicenseInfo } from '@mui/x-license';
import { AuthProvider } from 'app/containers/AuthProvider';
import { ThemeProvider } from 'styles/theme/ThemeProvider';
import './locales/i18n';

LicenseInfo.setLicenseKey(process.env.REACT_APP_MUI_LICENSE_KEY ?? '');

declare module '@mui/material/styles' {
  interface Palette {
    tooltip?: Palette['primary'];
    chips?: {
      red: React.CSSProperties['color'];
      green: React.CSSProperties['color'];
      blue: React.CSSProperties['color'];
      orange: React.CSSProperties['color'];
      brown: React.CSSProperties['color'];
    };
    indicators?: {
      error: React.CSSProperties['color'];
      warning: React.CSSProperties['color'];
      success: React.CSSProperties['color'];
    };
  }

  interface PaletteOptions {
    tooltip?: PaletteOptions['primary'];
    chips?: {
      red: React.CSSProperties['color'];
      green: React.CSSProperties['color'];
      blue: React.CSSProperties['color'];
      orange: React.CSSProperties['color'];
      brown: React.CSSProperties['color'];
    };
    indicators?: {
      error: React.CSSProperties['color'];
      warning: React.CSSProperties['color'];
      success: React.CSSProperties['color'];
    };
  }
}

// initialize the store. The store is configured under store/configureStore.ts
const store = configureAppStore();
const MOUNT_NODE = document.getElementById('root') as HTMLElement;
const root = createRoot(MOUNT_NODE);

interface Props {
  Component: typeof App;
}
const ConnectedApp = ({ Component }: Props) => (
  // This lets your app connect to the redux store
  <Provider store={store}>
    <HelmetProvider>
      <AuthProvider>
        <StyledEngineProvider injectFirst>
          <ThemeProvider>
            <React.StrictMode>
              <ToastContainer containerId="main" />
              <Component />
            </React.StrictMode>
          </ThemeProvider>
        </StyledEngineProvider>
      </AuthProvider>
    </HelmetProvider>
  </Provider>
);

const render = (Component: typeof App) => {
  // This not only renders the root react component called <App /> of your application
  // but it also renders it with <Provider />
  root.render(<ConnectedApp Component={Component} />);
};

render(App);

// Vite HMR
if (import.meta.hot) {
  import.meta.hot.accept();
}
